import { z } from "zod";
import { photoMimeTypeSchema } from "./photos";

// TES-1648 (Ö-1016) — the three patient photos on the written track.
//
// F02, "KANONISKT BESLUT 2026-10-28 — STANDARDISERADE PATIENTBILDER NÄR
// VÅRDMÖTE UTGÅR": a new-treatment patient who goes on without a care meeting
// leaves three fresh full-body photos (front, side, back). The doctor reviews
// them by hand and chooses "Godkänn bilder", "Begär nya bilder" or "Kräv
// vårdmöte"; a positive written decision waits for "Godkänn bilder". Nothing
// else waits for them: a completion request, a meeting or a negative decision
// is never blocked by missing photos.
//
// The images themselves are PatientPhoto rows (bytes in Postgres, encrypted
// under her data key, EXIF stripped, never a public URL). A round
// (AssessmentPhotoRound) groups one set of three with her attestation and the
// doctor's review.

export const ASSESSMENT_PHOTO_VIEWS = ["front", "side", "back"] as const;
export const assessmentPhotoViewSchema = z.enum(ASSESSMENT_PHOTO_VIEWS);
export type AssessmentPhotoView = z.infer<typeof assessmentPhotoViewSchema>;

/** PatientPhoto.context of a view's image: `assessment:front` etc. */
export function assessmentPhotoContext(view: AssessmentPhotoView): string {
  return `assessment:${view}`;
}

// F02 "Datakrav": review_status = pending|approved|retake_required|
// meeting_required. A round she has not sent yet has none (null).
export const ASSESSMENT_PHOTO_REVIEW_STATUSES = [
  "pending",
  "approved",
  "retake_required",
  "meeting_required",
] as const;
export const assessmentPhotoReviewStatusSchema = z.enum(
  ASSESSMENT_PHOTO_REVIEW_STATUSES,
);
export type AssessmentPhotoReviewStatus = z.infer<
  typeof assessmentPhotoReviewStatusSchema
>;

// F02 "Appflöde": "Varje bilduppladdning visar status: saknas, uppladdad,
// behöver tas om eller godkänd."
export const ASSESSMENT_PHOTO_VIEW_STATUSES = [
  "missing",
  "uploaded",
  "retake",
  "approved",
] as const;
export type AssessmentPhotoViewStatus =
  (typeof ASSESSMENT_PHOTO_VIEW_STATUSES)[number];

// The task as a whole, for her:
//   open             — something is missing, or the doctor asked for new
//                      photos; Hem shows "Lämna dina bilder";
//   submitted        — sent with her attestation, waiting for the doctor;
//   approved         — the doctor approved them;
//   meeting_required — the doctor required a care meeting instead.
export const ASSESSMENT_PHOTO_TASK_STATUSES = [
  "open",
  "submitted",
  "approved",
  "meeting_required",
] as const;
export type AssessmentPhotoTaskStatus =
  (typeof ASSESSMENT_PHOTO_TASK_STATUSES)[number];

// GET /patient-api/v1/assessment-photos. `required` false: the task does not
// apply to her (not on the written track); the app then shows nothing.
export const patientAssessmentPhotosViewSchema = z.object({
  required: z.boolean(),
  status: z.enum(ASSESSMENT_PHOTO_TASK_STATUSES),
  views: z.array(
    z.object({
      view: assessmentPhotoViewSchema,
      status: z.enum(ASSESSMENT_PHOTO_VIEW_STATUSES),
      uploadedAt: z.string().nullable(),
    }),
  ),
  // The doctor's short patient-facing reason with "Begär nya bilder" or
  // "Kräv vårdmöte"; null without one.
  doctorReason: z.string().nullable(),
  submittedAt: z.string().nullable(),
});
export type PatientAssessmentPhotosView = z.infer<
  typeof patientAssessmentPhotosViewSchema
>;

// POST /patient-api/v1/assessment-photos/:view
export const uploadAssessmentPhotoSchema = z.object({
  mimeType: photoMimeTypeSchema,
  dataBase64: z.string().min(1),
});
export type UploadAssessmentPhotoInput = z.infer<
  typeof uploadAssessmentPhotoSchema
>;

// The attestation she confirms before sending (F02 "Identitet"), verbatim.
export const ASSESSMENT_PHOTO_ATTESTATION_SV =
  "Jag bekräftar att bilderna föreställer mig och är tagna nyligen.";

// POST /patient-api/v1/assessment-photos/submit
export const submitAssessmentPhotosSchema = z.object({
  attested: z.literal(true),
});

// ---- the doctor's side ----

export const ASSESSMENT_PHOTO_REVIEW_OUTCOMES = [
  "approve",
  "retake",
  "require_meeting",
] as const;
export type AssessmentPhotoReviewOutcome =
  (typeof ASSESSMENT_PHOTO_REVIEW_OUTCOMES)[number];

// The buttons, in F02's words ("Läkar-admin").
export const ASSESSMENT_PHOTO_REVIEW_OUTCOME_LABELS: Record<
  AssessmentPhotoReviewOutcome,
  string
> = {
  approve: "Godkänn bilder",
  retake: "Begär nya bilder",
  require_meeting: "Kräv vårdmöte",
};

export const ASSESSMENT_PHOTO_REASON_MAX = 500;

// POST /employee-api/v1/patients/:id/assessment-photos/review. The reason is
// the "kort patientvänd orsak"; required when asking for new photos (she
// needs to know what to change), optional otherwise.
export const reviewAssessmentPhotosSchema = z
  .object({
    roundId: z.string().uuid(),
    outcome: z.enum(ASSESSMENT_PHOTO_REVIEW_OUTCOMES),
    reason: z.string().trim().max(ASSESSMENT_PHOTO_REASON_MAX).optional(),
  })
  .refine((v) => v.outcome !== "retake" || (v.reason ?? "").length > 0, {
    path: ["reason"],
    message: "Skriv kort till patienten vad som behöver tas om.",
  });
export type ReviewAssessmentPhotosInput = z.infer<
  typeof reviewAssessmentPhotosSchema
>;

export const clinicianAssessmentPhotoRoundSchema = z.object({
  id: z.string().uuid(),
  photos: z.array(
    z.object({
      view: assessmentPhotoViewSchema,
      photoId: z.string().uuid(),
      uploadedAt: z.string(),
    }),
  ),
  attestedAt: z.string().nullable(),
  submittedAt: z.string().nullable(),
  reviewStatus: assessmentPhotoReviewStatusSchema.nullable(),
  reviewedAt: z.string().nullable(),
  reviewedByName: z.string().nullable(),
  patientVisibleReason: z.string().nullable(),
});
export type ClinicianAssessmentPhotoRound = z.infer<
  typeof clinicianAssessmentPhotoRoundSchema
>;

// GET /employee-api/v1/patients/:id/assessment-photos
export const clinicianAssessmentPhotosViewSchema = z.object({
  // On the written track: the task applies to her at all.
  applies: z.boolean(),
  // The positive written decision waits for approved photos. False while
  // her app cannot take them yet (Simon's interim: the doctor decides
  // without photos until the step exists for her).
  requiredForDecision: z.boolean(),
  // Newest first; the first is the current round.
  rounds: z.array(clinicianAssessmentPhotoRoundSchema),
  // The current round is sent and waiting for the doctor.
  canReview: z.boolean(),
});
export type ClinicianAssessmentPhotosView = z.infer<
  typeof clinicianAssessmentPhotosViewSchema
>;

export const ASSESSMENT_PHOTO_JOURNAL_HEADING = "Bedömning av patientbilder";

const VIEW_LABELS_SV: Record<AssessmentPhotoView, string> = {
  front: "framifrån",
  side: "från sidan",
  back: "bakifrån",
};

/** "Framifrån", "Från sidan", "Bakifrån" — the doc's words for the views. */
export function assessmentPhotoViewLabel(view: AssessmentPhotoView): string {
  const label = VIEW_LABELS_SV[view];
  return label.charAt(0).toUpperCase() + label.slice(1);
}

const JOURNAL_OUTCOME_SV: Record<AssessmentPhotoReviewOutcome, string> = {
  approve: "Bilderna godkända.",
  retake: "Nya bilder begärda.",
  require_meeting: "Vårdmöte krävs, bilderna räcker inte som underlag.",
};

/**
 * The journal text the doctor copies into Webdoc under "Bedömning av
 * patientbilder": the three views with the date she sent them, her
 * attestation, and his outcome with its reason.
 */
export function assessmentPhotoJournalText(input: {
  outcome: AssessmentPhotoReviewOutcome;
  submittedAt: string | null;
  attestedAt: string | null;
  reason: string | null;
}): string {
  const day = (iso: string) =>
    new Date(iso).toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" });
  const lines = [
    ASSESSMENT_PHOTO_JOURNAL_HEADING,
    `Tre bilder (${ASSESSMENT_PHOTO_VIEWS.map((v) => VIEW_LABELS_SV[v]).join(
      ", ",
    )})${input.submittedAt ? `, inskickade ${day(input.submittedAt)}` : ""}.`,
  ];
  if (input.attestedAt) {
    lines.push(`Patienten har intygat: "${ASSESSMENT_PHOTO_ATTESTATION_SV}"`);
  }
  lines.push(JOURNAL_OUTCOME_SV[input.outcome]);
  if (input.reason) lines.push(`Orsak till patienten: ${input.reason}`);
  return lines.join("\n");
}
